import React, { useState, useEffect, useRef, useMemo } from 'react'
import moment from "moment"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  ComposedChart,
  ReferenceLine,
  LabelList,
} from "recharts"
import { map, values, reject, isNil, flatten } from "ramda"
import { 
  formatNumber, 
  randomColor,
  isNilOrEmpty,
  addDays,
  addMonths,
} from "src/main_app/utils/common"
import getSharedCustomerWaterUsage from 'src/main_app/services/apis/get-shared-customer-water-usage'
import getSharedConsumptionQuarterlyData from "src/main_app/utils/customer_utils/get-shared-consumption-quarterly-data"
import sharedCustomerHasSumsData from 'src/main_app/services/apis/shared-customer-has-sums-data'

const SharedUsageCharts = ({ customerId, customer, shareToken }) => {
  // All useState hooks first
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dataWeekly, setDataWeekly] = useState([])
  const [meterNames, setMeterNames] = useState([])
  const [ticks, setTicks] = useState([])
  const [dataQuarterly, setDataQuarterly] = useState([])
  const [meterNamesQuarterly, setMeterNamesQuarterly] = useState([])
  const [hasRealtimeDataState, setHasRealtimeDataState] = useState(false)
  const [dateRangeDataWeekly, setDateRangeDataWeekly] = useState(() => {
    const today = new Date()
    const toDate = addDays(today, -1)
    const fromDate = addDays(today, -8)
    return [fromDate, toDate]
  })
  const [dateRangeQuarterly, setDateRangeQuarterly] = useState(() => {
    const today = new Date()
    const toDate = addDays(today, -1)
    const fromDate = addMonths(today, -12) // Keep 12 months for more data
    return [fromDate, toDate]
  })
  const [aspect, setAspect] = useState(getAspect(window.innerWidth))

  // All useMemo hooks
  const hasRealtimeData = hasRealtimeDataState && !isNilOrEmpty(dataWeekly)

  const allSumData = useMemo(() => {
    const dataUsage = []
    if (!hasRealtimeData) return dataUsage

    for (const itemDataWeekly of dataWeekly) {
      const { date, displayDate, water_cost_usd, ...dataSumUsage } = itemDataWeekly
      console.log('itemDataWeekly keys:', Object.keys(itemDataWeekly))
      console.log('dataSumUsage keys:', Object.keys(dataSumUsage))
      console.log('dataSumUsage values:', values(dataSumUsage))
      dataUsage.push(values(dataSumUsage))
    }
    const result = reject(isNil, flatten(dataUsage))
    console.log('allSumData result:', result)
    console.log('allSumData max:', Math.max(...result))
    return result
  }, [dataWeekly, hasRealtimeData])

  const dataWeeklyMax = useMemo(
    () => allSumData.length > 0 ? Math.ceil(Math.max(...allSumData) * 10) / 10 : 0,
    [allSumData]
  )

  // All useEffect hooks
  useEffect(() => {
    import("../../scenes/customer_detail_page/tabs/customers_usage_page/style.scss");
  }, [])

  useEffect(() => {
    const handleResize = () => setAspect(getAspect(window.innerWidth))
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Helper functions
  function getAspect(width) {
    if (width < 480) return 10 / 7 // taller for mobile
    if (width < 768) return 10 / 5 // tablet
    return 10 / 3 // desktop
  }

  // Weekly data fetcher (matching customer detail page logic)
  const getDataWeekly = async (startDate, endDate) => {
    if (!shareToken || !customerId) return
    
    try {
      const fromDate = startDate || dateRangeDataWeekly[0]
      const toDate = endDate || dateRangeDataWeekly[1]
      
      const params = {
        shareToken,
        from: moment(fromDate).format("YYYY-MM-DD"),
        to: moment(toDate).format("YYYY-MM-DD")
      }
      
      const response = await getSharedCustomerWaterUsage(customerId, params)
      
      let rawData = []
      let costDateRange = []
      
      if (response.data && response.data.data) {
        rawData = response.data.data
        costDateRange = response.data.costDateRange || []
      } else if (response.data) {
        rawData = response.data
      }
      
      // Process data like customer detail page
      const ticks = []
      const allMeterNames = []
      
      // Map cost data
      const costDateRangeMapped = Array.isArray(costDateRange) ? costDateRange.map((item) => {
        const { time, water_cost_usd } = item
        const utcTime = moment(time).utcOffset(0, true)
        const utcUnix = utcTime.unix()
        
        if (ticks.length === 0 || utcUnix - ticks[ticks.length - 1] >= 86400) {
          ticks.push(utcUnix)
        }
        
        // Get total days in month for cost calculation
        const date = new Date(time)
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        const totalDays = new Date(year, month, 0).getDate()
        
        return {
          date: utcUnix,
          displayDate: utcTime.format("ddd MMM DD hh:mm a"),
          water_cost_usd: water_cost_usd * totalDays,
        }
      }) : []
      
      // Map usage data
      const arrDataWeekly = Array.isArray(rawData) ? rawData.map((item) => {
        const { time, ...metersData } = item
        
        if (Object.keys(metersData).length === 0) return { time }
        
        const utcTime = moment(time).utcOffset(0, true)
        const utcUnix = utcTime.unix()
        
        if (ticks.length === 0 || utcUnix - ticks[ticks.length - 1] >= 86400) {
          ticks.push(utcUnix)
        }
        
        const combinedData = {
          date: utcUnix,
          displayDate: utcTime.format("ddd MMM DD hh:mm a"),
        }
        
        // Process meter data
        for (const meterName in metersData) {
          if (!Object.hasOwnProperty.call(metersData, meterName)) continue
          allMeterNames.push(meterName)
          const sumUsage = metersData[meterName]
          combinedData[meterName] = sumUsage
            ? Math.round(sumUsage * 100) / 100
            : null
        }
        
        return combinedData
      }) : []
      
      // Set unique meter names
      const setOfMeterNames = [...new Set(allMeterNames)]
      setMeterNames(setOfMeterNames)
      setTicks(ticks)
      
      // Fill missing meter data with 0
      const processedData = arrDataWeekly.map((item) => {
        for (const meterName of setOfMeterNames) {
          if (item[meterName] == null) {
            item[meterName] = 0
          }
        }
        return item
      })
      
      // Merge with cost data
      const dataWithCost = processedData.map((item) => {
        const day = item.date
        const cost = costDateRangeMapped.find((c) => c.date === day)
        const valueCost = cost ? cost.water_cost_usd : null
        return {
          ...item,
          water_cost_usd: valueCost,
        }
      })
      
      console.log('Weekly data processed:', dataWithCost)
      console.log('Meter names:', setOfMeterNames)
      setDataWeekly(dataWithCost)
      
    } catch (error) {
      console.error('Error fetching weekly data:', error)
      setError('Failed to load weekly usage data')
    }
  }

  // Quarterly data fetcher (matching customer detail page logic)
  const getDataQuarterly = async (startDate, endDate) => {
    if (!shareToken || !customerId) return
    
    try {
      const fromDate = startDate || dateRangeQuarterly[0]
      const toDate = endDate || dateRangeQuarterly[1]
      
      const { data, costMonthRequest } = await getSharedConsumptionQuarterlyData(
        customerId,
        shareToken,
        moment(fromDate).format("YYYY-MM-DD"),
        moment(toDate).format("YYYY-MM-DD")
      )
      
      console.log('Raw quarterly data:', data)
      console.log('Raw quarterly costMonthRequest:', costMonthRequest)
      
      // Process data like customer detail page
      const costMonthReqMapped = Array.isArray(costMonthRequest) 
        ? costMonthRequest.map((result) => ({
            water_cost_usd: result.water_cost_usd || 0,
            date: result.month,
            startDate: result.month,
          }))
        : []

      const usageConsumptionMapped = Array.isArray(data)
        ? data.map((result) => ({
            usage: Math.round((result.total_consumption || 0) * 100) / 100,
            date: result.month,
            startDate: result.month,
            consumptions: result.arr_consumption || [],
            merterSerials: result.arr_meter_serial || [],
          }))
        : []

      // Merge usage data with cost data
      const mergedData = usageConsumptionMapped.map((item) => {
        const cost = costMonthReqMapped.find((c) => c.date === item.date)
        return {
          ...item,
          water_cost_usd: cost ? cost.water_cost_usd : 0,
        }
      })

      console.log('Quarterly data processed:', mergedData)
      setDataQuarterly(mergedData)
      
      // Extract meter names for quarterly
      if (mergedData && mergedData.length > 0) {
        const names = mergedData[0].merterSerials || []
        setMeterNamesQuarterly(names)
      }
    } catch (error) {
      console.error('Error fetching quarterly data:', error)
      setError('Failed to load quarterly usage data')
    }
  }

  // Check if customer has sums data first (like customer detail page)
  const checkCustomerHasSumsData = async () => {
    if (!customerId || !shareToken) return false
    
    try {
      const response = await sharedCustomerHasSumsData({
        shareToken,
        customer_ids: customerId
      })
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        return response.data.data[0].has_sums_data
      }
      return false
    } catch (error) {
      console.error('Error checking customer has sums data:', error)
      return false
    }
  }

  // Load all data - useEffect after functions are defined
  useEffect(() => {
    if (!customerId || !shareToken) return
    
    const loadAllData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // First check if customer has sums data (like customer detail page)
        const hasSumsData = await checkCustomerHasSumsData()
        
        if (hasSumsData) {
          setHasRealtimeDataState(true)
          await Promise.all([
            getDataWeekly(),
            getDataQuarterly(),
          ])
        } else {
          // Still load quarterly data even if no realtime data
          await getDataQuarterly()
        }
      } catch (error) {
        console.error('Error loading usage data:', error)
        setError('Failed to load usage data')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadAllData()
  }, [customerId, shareToken])

  // Constants (matching customer detail page)
  const refLineY = dataWeeklyMax * 0.8
  
  const tooltipContentStyle = {
    boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
    borderRadius: "4px",
    border: "none",
    textAlign: "center",
  }

  const tooltipItemStyle = {
    color: "#05d6f",
    fontSize: "12px",
    fontWeight: "500",
  }

  const yFormatter = (tickItem) => formatNumber(tickItem) + " "
  
  const ticksFormatter = (value, index) =>
    moment(value * 1000).format("ddd MMM DD")

  const yLabelSUMs = {
    value: "L",
    angle: -90,
    position: "top",
    dy: 170,
    dx: -10,
  }

  const yLabel = {
    value: "Usage (kL)",
    angle: -90,
    position: "top",
    dy: 170,
    dx: -15,
  }

  // Tooltip renderers (exact copy from customer detail page)
  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    return (
      <div
        className="custom-line-tooltip"
        style={{
          padding: "5px",
          backgroundColor: "#fff",
          boxShadow: "0 0 8px 8px rgb(0 0 0 / 7%)",
          borderRadius: "4px",
        }}
      >
        <h5 className="mb-0">{payload[0].payload.displayDate}</h5>
        <br />
        {payload.map((payloadItem, index) => (
          <div
            key={index + ""}
            style={{
              color: payloadItem.stroke,
            }}
          >
            {payloadItem.dataKey === "water_cost_usd" ? (
              <>
                {payloadItem.name}: ${formatNumber(payloadItem.value)}
                {payloadItem.unit}
              </>
            ) : (
              <>
                {payloadItem.name}: {formatNumber(payloadItem.value)}{" "}
                {payloadItem.unit}
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderTooltip2 = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null

    return (
      <div
        className="custom-line-tooltip"
        style={{
          backgroundColor: "#fff",
          minWidth: "260px",
          boxShadow: "0 0 8px 8px rgb(0 0 0 / 5%)",
          overflow: "auto",
          maxHeight: "410px",
          position: "relative",
          pointerEvents: "auto",
        }}
      >
        <div style={{ padding: "20px 20px 0px 20px" }}>
          <p style={{ fontWeight: "bold", marginBottom: "-2px" }}>
            {payload[0].payload.startDate} - {payload[0].payload.date}
          </p>
          <p style={{ color: "#727f94" }}>
            Total usage: {formatNumber(payload[0].payload.usage)} kL
          </p>
        </div>
        <hr />
        <div style={{ paddingBottom: "15px" }}>
          <table style={{ fontSize: "13px" }}>
            <tr>
              <th style={{ padding: "5px 0px 5px 20px" }}>SERIAL</th>
              <th>USAGE</th>
            </tr>
            {payload[0].payload.merterSerials?.map((item, index) => (
              <tr key={index + ""}>
                <td style={{ padding: " 5px 0 5px 20px", color: "#727f94" }}>
                  {item}
                </td>
                <td style={{ color: "#727f94" }}>
                  {payload[0].payload.consumptions[index] >= 0
                    ? formatNumber(payload[0].payload.consumptions[index])
                    : 0}{" "}
                  kL
                </td>
              </tr>
            ))}
          </table>
        </div>
      </div>
    )
  }

  const renderTooltipQuarterlyUsage = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const data = payload[0].payload
    
    return (
      <div
        className="custom-line-tooltip"
        style={{
          backgroundColor: "#fff",
          minWidth: "260px",
          boxShadow: "0 0 8px 8px rgb(0 0 0 / 5%)",
          overflow: "auto",
          maxHeight: "410px",
          position: "relative",
          pointerEvents: "auto",
          borderRadius: "8px",
        }}
      >
        <div style={{ padding: "16px 20px" }}>
          <div style={{ padding: "20px 20px 0px 20px" }}>
            <p style={{ fontWeight: "bold", marginBottom: "-2px" }}>
              {payload[0].payload.startDate}
            </p>
            <p style={{ color: "#727f94", marginBottom: "-2px" }}>
              Meter id: {payload[0].payload.merterSerials?.[0]}
            </p>
            <p style={{ color: "#727f94" }}>
              Total usage: {formatNumber(payload[0].payload.usage)} kL
            </p>
            <p style={{ color: "#727f94" }}>
              Water Cost: ${data.water_cost_usd}/month
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading usage data...</span>
        </div>
        <p className="mt-2">Loading usage data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    )
  }

  // Check if we have actual realtime data (matching customer detail page)
  const checkHasRealtimeData = []
  if (hasRealtimeData) {
    for (const itemDataWeekly of dataWeekly) {
      if (itemDataWeekly["date"] != undefined) {
        checkHasRealtimeData.push(itemDataWeekly)
        break
      }
    }
  }

  return (
    <div>
      {/* Weekly Usage Chart - matching customer detail page style */}
      {hasRealtimeData && (
        <div className="py-4">
          <h3 className="sub-title">Weekly Usage</h3>
          <div className="form-group">
            <label>Date range</label>
            <div className="form-control" style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}>
              {moment(dateRangeDataWeekly[0]).format('DD MMM YYYY')} - {moment(dateRangeDataWeekly[1]).format('DD MMM YYYY')}
            </div>
          </div>
          
          {isNilOrEmpty(checkHasRealtimeData) ? (
            <div className="no-item-message">
              {" "}
              There is currently no usage data available.
            </div>
          ) : (
            <>
              <div className="card" id="realtime-chart">
              <div className="card-body">
                <ResponsiveContainer width="100%" aspect={aspect}>
                  <LineChart
                    height={180}
                    data={dataWeekly}
                    margin={{
                      top: 20,
                      right: 40,
                      left: -15,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid stroke="#e9ebf1" />
                    <XAxis
                      dataKey="date"
                      stroke="#e9ebf1"
                      type="category"
                      interval="preserveStartEnd"
                      ticks={ticks}
                      tickFormatter={ticksFormatter}
                    />
                    <YAxis
                      stroke="#e9ebf1"
                      domain={[0, dataWeeklyMax]}
                      label={yLabelSUMs}
                    />
                    
                    {/* Usage lines for each meter */}
                    {meterNames.map((meterName, index) => (
                      <Line
                        key={index}
                        type="monotone"
                        dataKey={meterName}
                        name={meterName}
                        stroke={
                          index === 0
                            ? customer?.is_recycle
                              ? "#C8A2C8"
                              : "#09A3B3"
                            : randomColor(index)
                        }
                        dot={false}
                        unit=" L"
                      />
                    ))}

                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        itemStyle={tooltipItemStyle}
                        content={renderTooltip}
                      />
                    <Legend iconType="circle" margin={{ top: 50 }} />
                  </LineChart>
                </ResponsiveContainer>
                
                </div>
              </div>
              
              {customer?.is_recycle && (
                <div className="mt-2 text-center" style={{ fontSize: 12 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginRight: 12,
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 3,
                        backgroundColor: "#C8A2C8",
                        display: "inline-block",
                        marginRight: 6,
                      }}
                    />
                    Recycled meter
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Quarterly Usage Chart - exact copy from customer detail page */}
      {!isNilOrEmpty(dataQuarterly) && (
        <div className="py-4">
          <h3 className="sub-title">Quarterly usage</h3>
          <div className="form-group">
            <label>Date range</label>
            <div className="form-control" style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}>
              {moment(dateRangeQuarterly[0]).format('DD MMM YYYY')} - {moment(dateRangeQuarterly[1]).format('DD MMM YYYY')}
            </div>
          </div>
          
          {isNilOrEmpty(dataQuarterly) ? (
            <div className="no-item-message">
              {" "}
              There is currently no usage data available.
            </div>
          ) : (
          <>
            <div className="card" id="quarterly-usage-chart">
              <div className="card-body">
                <ResponsiveContainer width="100%" aspect={aspect}>
                  <ComposedChart
                    data={dataQuarterly}
                    margin={{ top: 20, right: 5, left: -15, bottom: 5 }}
                  >
                    <CartesianGrid stroke="#e9ebf1" />
                    <XAxis dataKey="date" stroke="#e9ebf1" type="category" />

                    {/* Left axis for usage */}
                    <YAxis
                      yAxisId="left"
                      dataKey="usage"
                      stroke="#e9ebf1"
                      tickFormatter={yFormatter}
                      label={yLabel}
                    />

                    <Tooltip
                      contentStyle={{
                        boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
                        borderRadius: "4px",
                        border: "none",
                        pointerEvents: "auto",
                      }}
                      itemStyle={{
                        color: "#05d6f",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                      content={renderTooltipQuarterlyUsage}
                    />

                    {/* Bar — usage */}
                    <Bar
                      yAxisId="left"
                      dataKey="usage"
                      fill="#09A3B3"
                      radius={[5, 5, 0, 0]}
                      barSize={24}
                      unit=" kL"
                      name="Total Usage (kL)"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
          )}
        </div>
      )}

      {/* No data message */}
      {isNilOrEmpty(dataWeekly) && isNilOrEmpty(dataQuarterly) && !isLoading && (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          No usage data available for this customer.
        </div>
      )}
    </div>
  )
}

export default SharedUsageCharts